import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { FaPlus, FaRegTrashAlt } from "react-icons/fa";
import { fieldCls } from "components/InlineTable";
import HeaderButtons from "components/Button/HeaderButtons";
import Button from "components/Button";
import BackButton from "components/Button/BackButton";
import Loader from "components/Loader";
import { useToaster } from "contexts/toasterContext";
import { getApiErrorMessage } from "util/errorMessage";
import feedbackManager from "services/Feedback/feedbackManager";
import {
  Feedback,
  formatPeriod,
  getQuarterMonths,
  getWritableQuarters,
  KraArea,
  MAX_ACHIEVEMENT_LENGTH,
  MAX_GOAL_ROWS,
  parseQuarterKey,
  QuarterRef,
  SelfFeedbackRequest,
} from "services/Feedback/feedbackInterface";
import {
  calculateTotalWeightage,
  countFilledRows,
  createEmptyGoalRow,
  getAddGoalBlockReason,
  getObjectiveOptions,
  GoalRowDraft,
  toGoalRowDrafts,
  toItemRequests,
  validateSelfFeedback,
} from "./feedbackFormUtils";
import { FIELD_LABEL_CLS, RatingSelect, WeightageMeter } from "./components/FeedbackBits";
import { KraAreaSelect, KraObjectiveSelect } from "./components/KraSelects";

/**
 * Goal sheet editor — a routed SCREEN, not a modal.
 *
 * CHANGE: this replaces `SelfFeedbackForm` inside `GenericModal`. Five goal
 * rows never fitted a centred dialog: one row filled the viewport, the live
 * weightage meter scrolled out of sight exactly when the user needed it, and
 * the Submit button sat below the fold. On a screen the meter and the actions
 * are pinned and only the rows scroll.
 *
 * NOTE: one component for both modes, via the `mode` prop — the same shape as
 * `payrole/salaryStructure/SalaryStructureFormPage`, which is this app's
 * existing precedent for a modal that became a screen.
 *
 *   create → /admin/feedback/goal-sheet/new/:quarterKey   (POST /self)
 *   edit   → /admin/feedback/goal-sheet/:feedbackId       (PUT  /self/{id})
 *
 * The server independently enforces every rule here: POST/PUT /self is always
 * about the caller, and only a DRAFT is editable.
 */
export interface GoalSheetFormPageProps {
  mode: "create" | "edit";
}

const LIST_PATH = "/admin/feedback";

/**
 * Every field reserves the same error height, so a message appearing on one
 * can't shunt its neighbours down and re-flow the card.
 */
const CellError: React.FC<{ message?: string }> = ({ message }) => (
  <div className="mt-0.5 h-4">
    <p className={`text-xs font-medium leading-tight ${message ? "text-error" : "invisible"}`}>
      {message ?? "·"}
    </p>
  </div>
);

const GoalSheetFormPage: React.FC<GoalSheetFormPageProps> = ({ mode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ quarterKey?: string; feedbackId?: string }>();
  const { addToast } = useToaster();

  const isEdit = mode === "edit";

  /**
   * On edit the record usually arrives through router state, so opening from a
   * card costs no round trip. A hard refresh or a pasted URL has no state, so
   * it is fetched by id instead of bouncing the user back to the list.
   */
  const passedFeedback = (location.state as { feedback?: Feedback } | null)?.feedback ?? null;

  const [record, setRecord] = useState<Feedback | null>(isEdit ? passedFeedback : null);
  const [loading, setLoading] = useState(isEdit && !passedFeedback);
  const [loadError, setLoadError] = useState("");
  const [areas, setAreas] = useState<KraArea[]>([]);

  const [rows, setRows] = useState<GoalRowDraft[]>(() => toGoalRowDrafts(passedFeedback?.items));
  /**
   * CHANGE: the "Overall comment" and "Self rating" FIELDS were removed from
   * this form — a goal sheet is written at the START of a quarter, so summing
   * up the quarter and scoring yourself has nothing to answer yet.
   *
   * NOTE: the state is deliberately KEPT, not deleted. PUT /self/{id} replaces
   * the record, so a sheet that already carries an overall comment or rating
   * would have them wiped the next time it was saved if this sent nothing.
   * Seeded from the loaded record and passed straight back through, so editing
   * a sheet preserves whatever is there; a new sheet simply sends null.
   * Whoever adds the end-of-quarter review screen should move these there.
   */
  const [overallComment, setOverallComment] = useState(passedFeedback?.overallComment ?? "");
  const [overallRating, setOverallRating] = useState<number | null>(
    passedFeedback?.overallRating ?? null,
  );

  const [saving, setSaving] = useState<"draft" | "submit" | null>(null);
  const [validation, setValidation] = useState(() =>
    validateSelfFeedback({ rows: [], submit: false }),
  );
  const [apiError, setApiError] = useState("");

  /**
   * The quarter: from the record when editing (it can never change), otherwise
   * from the URL. An unparseable key falls back to the current quarter rather
   * than erroring — the server would reject a bad one anyway, and the two
   * writable quarters are the only values the UI ever links to.
   */
  const quarter = useMemo<QuarterRef>(() => {
    if (record) return { year: record.year, quarter: record.quarter };
    return parseQuarterKey(params.quarterKey ?? "") ?? getWritableQuarters()[0];
  }, [record, params.quarterKey]);

  const period = record?.period ?? formatPeriod(quarter);

  // ── Load ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    feedbackManager
      .getKraAreas(false)
      .then((response) => setAreas(Array.isArray(response.data) ? response.data : []))
      .catch((error) => addToast(getApiErrorMessage(error, "Failed to load KRA areas"), "error"));
  }, [addToast]);

  useEffect(() => {
    if (!isEdit || passedFeedback || !params.feedbackId) return;

    let cancelled = false;
    setLoading(true);

    feedbackManager
      .getFeedback(Number(params.feedbackId))
      .then((response) => {
        if (cancelled) return;
        const loaded = response.data;
        setRecord(loaded);
        setRows(toGoalRowDrafts(loaded.items));
        setOverallComment(loaded.overallComment ?? "");
        setOverallRating(loaded.overallRating);
      })
      .catch((error) => {
        if (!cancelled) setLoadError(getApiErrorMessage(error, "Failed to load this goal sheet."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isEdit, passedFeedback, params.feedbackId]);

  // ── Derived ───────────────────────────────────────────────────────────────

  const totalWeightage = useMemo(() => calculateTotalWeightage(rows), [rows]);
  const isBusy = saving !== null;
  const startedRows = countFilledRows(rows);

  /**
   * Null when another goal may be added; otherwise the reason it may not, which
   * is printed beside the button so the disabled state is never a mystery.
   */
  const addBlockReason = useMemo(() => getAddGoalBlockReason(rows), [rows]);

  // ── Row editing ───────────────────────────────────────────────────────────

  const updateRow = useCallback((uid: string, patch: Partial<GoalRowDraft>) => {
    setRows((previous) => previous.map((row) => (row.uid === uid ? { ...row, ...patch } : row)));
    setApiError("");
  }, []);

  /** Changing the area invalidates the objective, so it is cleared with it. */
  const handleAreaChange = (uid: string, areaId: number | null) => {
    updateRow(uid, {
      areaId,
      objectiveId: null,
      savedAreaName: undefined,
      savedObjectiveName: undefined,
    });
  };

  const addRow = () => {
    // Guarded here too, not just on the button — a stale click or an Enter key
    // on a focused-then-disabled button must not slip a row through.
    if (getAddGoalBlockReason(rows) !== null) return;
    setRows((previous) => [...previous, createEmptyGoalRow()]);
  };

  const removeRow = (uid: string) => {
    setRows((previous) => {
      const next = previous.filter((row) => row.uid !== uid);
      // Never leave the sheet with no row at all — an empty editor looks broken.
      return next.length > 0 ? next : [createEmptyGoalRow()];
    });
  };

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSave = async (submit: boolean) => {
    const result = validateSelfFeedback({ rows, submit });
    setValidation(result);
    setApiError("");
    if (!result.isValid) return;

    setSaving(submit ? "submit" : "draft");

    const payload: SelfFeedbackRequest = {
      overallComment: overallComment.trim() === "" ? null : overallComment.trim(),
      overallRating,
      items: toItemRequests(rows),
      submit,
    };

    // The quarter is fixed once the sheet exists, so it is sent only on create.
    if (!record) {
      payload.year = quarter.year;
      payload.quarter = quarter.quarter;
    }

    try {
      await (record
        ? feedbackManager.updateSelfFeedback(record.id, payload)
        : feedbackManager.createSelfFeedback(payload));

      addToast(
        submit
          ? `Self feedback for ${period} submitted for approval`
          : `Draft saved for ${period}`,
        "success",
      );
      goBack();
    } catch (error) {
      setApiError(getApiErrorMessage(error, "Failed to save self feedback"));
      setSaving(null);
    }
  };

  /**
   * NOTE: an explicit path, not `navigate(-1)`. A deep link or a refresh has no
   * history to go back to, and `viewingOwn` puts a reviewer back on their own
   * screen instead of dumping them on the team roll-up they never came from.
   */
  const goBack = () => navigate(LIST_PATH, { state: { viewingOwn: true } });

  // ── Guards ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex min-h-[240px] items-center justify-center">
        <Loader />
      </div>
    );
  }

  /**
   * Only a DRAFT self sheet can be edited, and `editable` is the server's own
   * verdict — so a submitted sheet reached by a stale link returns to the list
   * rather than rendering a form whose every save would 400.
   */
  if (isEdit && record && (record.kind !== "SELF" || !record.editable)) {
    return <Navigate to={LIST_PATH} replace />;
  }

  if (isEdit && !record) {
    return (
      <div className="p-4">
        <div className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-error dark:bg-red-900/20">
          {loadError || "This goal sheet could not be opened."}
        </div>
        <HeaderButtons type="button" variant="tertiary" size="small" className="mt-3" onClick={goBack}>
          Back to Review
        </HeaderButtons>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  /*
   * CHANGE: was h-[calc(100vh-105px)] / h-[calc(100vh-75px)] chosen by whether
   * the plan banner is up. Both numbers were guesses at chrome this page never
   * measures, and they left dead white space below the pinned action bar.
   *
   * The admin shell already hands every page a correctly sized scroll box —
   * SideBar wraps <Outlet /> in "flex-1 min-h-0 overflow-auto" inside a flex
   * column that the banner is a sibling of — so h-full fills exactly that box
   * and the banner is accounted for without this page knowing about it. Same
   * fix, and the same reasoning, as the NOTE in pages/tasks/index.tsx.
   *
   * NOTE: this is why BannerContext is no longer read here — and the context
   * was the wrong signal anyway, since the banner's py-1 wrapper keeps taking
   * space after the banner itself is dismissed.
   *
   * NOTE: no min-h. A minimum taller than the viewport is the one thing that
   * makes this page overflow its box, which would put a scrollbar on the shell
   * AND on the body region at the same time. The column already degrades
   * safely without one: the header and footer are shrink-0 and the body is
   * flex-1 min-h-0, so a short window just gives the body less room to scroll.
   */
  return (
    <div
      className="flex h-full flex-col text-slate-900 transition-colors duration-300 dark:text-white bg-white dark:bg-gray-800"
    >
      {/* ── Header: never scrolls, so the meter stays in view ───────────── */}
      <div className="shrink-0 border-b border-gray-200 px-3 py-2.5 dark:border-gray-700 sm:px-4">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex min-w-0 items-center gap-2.5">
            <BackButton onClick={goBack} label="Back to Review" />
            <div className="min-w-0">
              <h1 className="truncate text-base font-semibold">
                {isEdit ? "Edit goal sheet" : "New goal sheet"}
                <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                  {period}
                </span>
                {/* `period` is the server's own string ("Q4 2026"); the month
                    range is derived from the quarter number beside it, so the
                    server stays the source of truth for the label itself. */}
                <span className="ml-1.5 text-xs font-normal text-gray-400 dark:text-gray-500">
                  {getQuarterMonths(quarter.quarter)}
                </span>
              </h1>
              <p className="truncate text-xs text-gray-400 dark:text-gray-500">
                Up to {MAX_GOAL_ROWS} goals · weightage must total 100% to submit
              </p>
            </div>
          </div>

          {/* The rule the submit depends on, pinned where it can't scroll away. */}
          <div className="min-w-[220px] max-w-sm flex-1">
            <WeightageMeter total={totalWeightage} />
          </div>
        </div>
      </div>

      {/* ── Body: the only scroller ─────────────────────────────────────── */}
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-4">
        {apiError && (
          <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-error dark:bg-red-900/20">
            {apiError}
          </div>
        )}
        {validation.formError && (
          <div className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
            {validation.formError}
          </div>
        )}

        {/* ── Goal cards ──────────────────────────────────────────────────
            CHANGE: was a 7-column InlineTable with minWidth={1060}. A grid is
            the wrong shape for this form: the KPI / achievement field holds up
            to 2000 characters but was squeezed into a 2-row box, Wt % and
            Rating were stranded 900px from the goal they belong to, and the
            1060px floor forced sideways scrolling on a laptop — which is why
            the Submit button was clipped off-screen.

            Each goal is now a card that reads top-down: its number, then area
            and objective, then the numbers, then the achievement across the
            full width. This is the same shape FeedbackCard already uses for
            the read-only checklist, so composing a goal and reading one back
            finally look like the same feature.

            NOTE: widths are flex-basis + min-width, never a `base → sm:` pair.
            In this project's built CSS the unprefixed utility is emitted after
            the sm: media block, so those pairs silently never apply. */}
        <ol className="flex flex-col gap-2.5">
          {rows.map((row, index) => {
            const errors = validation.rowErrors[row.uid] ?? {};
            const objectives = getObjectiveOptions({
              areas,
              areaId: row.areaId,
              rows,
              currentRowUid: row.uid,
            });

            const areaId = `goal-${row.uid}-area`;
            const objectiveId = `goal-${row.uid}-objective`;
            const weightageId = `goal-${row.uid}-weightage`;
            const ratingId = `goal-${row.uid}-rating`;
            const achievementId = `goal-${row.uid}-achievement`;

            return (
              <li
                key={row.uid}
                className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
              >
                {/* Card header — which goal this is, and how to drop it. */}
                <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-1.5 dark:border-gray-700">
                  <span className="flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-100 text-[11px] tabular-nums text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                      {index + 1}
                    </span>
                    Goal {index + 1}
                  </span>

                  <button
                    type="button"
                    onClick={() => removeRow(row.uid)}
                    disabled={isBusy}
                    title="Remove this goal"
                    aria-label={`Remove goal ${index + 1}`}
                    className="rounded p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-error disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-red-900/20"
                  >
                    <FaRegTrashAlt size={12} />
                  </button>
                </div>

                <div className="p-3">
                  {/* The three short fields share one wrapping row. */}
                  <div className="flex flex-wrap items-start gap-x-2.5 gap-y-1">
                    <div className="min-w-[11rem] flex-1">
                      <label className={FIELD_LABEL_CLS} htmlFor={areaId}>
                        Area
                      </label>
                      <KraAreaSelect
                        id={areaId}
                        value={row.areaId}
                        areas={areas}
                        savedName={row.savedAreaName}
                        disabled={isBusy}
                        hasError={!!errors.areaId}
                        onChange={(value) => handleAreaChange(row.uid, value)}
                      />
                      <CellError message={errors.areaId} />
                    </div>

                    <div className="min-w-[12rem] flex-1">
                      <label className={FIELD_LABEL_CLS} htmlFor={objectiveId}>
                        Objective
                      </label>
                      <KraObjectiveSelect
                        id={objectiveId}
                        value={row.objectiveId}
                        objectives={objectives}
                        areaChosen={row.areaId !== null}
                        savedName={row.savedObjectiveName}
                        disabled={isBusy}
                        hasError={!!errors.objectiveId}
                        onChange={(value) => updateRow(row.uid, { objectiveId: value })}
                      />
                      <CellError message={errors.objectiveId} />
                    </div>

                    <div className="w-24 shrink-0">
                      <label className={FIELD_LABEL_CLS} htmlFor={weightageId}>
                        Wt %
                      </label>
                      <input
                        id={weightageId}
                        type="number"
                        min={1}
                        max={100}
                        step={1}
                        value={row.weightage}
                        disabled={isBusy}
                        onChange={(e) => updateRow(row.uid, { weightage: e.target.value })}
                        onWheel={(e) => (e.target as HTMLInputElement).blur()}
                        className={fieldCls(!!errors.weightage)}
                      />
                      <CellError message={errors.weightage} />
                    </div>

                    <div className="w-28 shrink-0">
                      <label className={FIELD_LABEL_CLS} htmlFor={ratingId}>
                        Rating
                      </label>
                      <RatingSelect
                        id={ratingId}
                        value={row.rating}
                        disabled={isBusy}
                        onChange={(value) => updateRow(row.uid, { rating: value })}
                      />
                      <CellError />
                    </div>
                  </div>

                  {/* The long field gets the whole card width and real height. */}
                  <div>
                    <label className={FIELD_LABEL_CLS} htmlFor={achievementId}>
                      Achievement
                    </label>
                    <textarea
                      id={achievementId}
                      rows={3}
                      value={row.achievement}
                      maxLength={MAX_ACHIEVEMENT_LENGTH}
                      disabled={isBusy}
                      placeholder="How this goal is measured, and what you delivered"
                      onChange={(e) => updateRow(row.uid, { achievement: e.target.value })}
                      className={`${fieldCls(!!errors.achievement)} resize-y`}
                    />
                    <CellError message={errors.achievement} />
                  </div>
                </div>
              </li>
            );
          })}
        </ol>

        {/* CHANGE: the add control is now a full-width dashed strip rather than
            a small button floating in white space — it reads as "another goal
            goes here", which is what fills the gap the old layout left below a
            one-goal sheet. The button itself is still the shared HeaderButtons;
            only its surround is new. */}
        <div className="mt-2.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-xl border border-dashed border-gray-300 px-3 py-2.5 dark:border-gray-600">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <HeaderButtons
              type="button"
              variant="tertiary"
              size="small"
              onClick={addRow}
              disabled={isBusy || addBlockReason !== null}
              icon={<FaPlus size={11} />}
            >
              Add goal
            </HeaderButtons>

            <span className="text-xs text-gray-400 dark:text-gray-500">
              {startedRows} of {MAX_GOAL_ROWS} goals
            </span>
          </div>

          {/* Says WHY the button is dead. Without this a disabled control with a
              full-looking form reads as a broken page. */}
          {addBlockReason && !isBusy && (
            <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
              {addBlockReason}
            </span>
          )}
        </div>
      </div>

      {/* ── Actions: pinned, so Submit is never below the fold ──────────── */}
      {/* CHANGE: `components/Button` at the app's standard size, in a 74px bar —
          lifted from SalaryStructureFormPage, the existing routed full-page
          add/edit form, so both screens' action bars are identical.

          CHANGE: `pr-24` removed — it never worked. In the built CSS `.pr-24`
          is emitted at byte ~46.8k but `.px-4` at ~251k (index.css is followed
          by flowbite.css, which re-emits the base utilities), so with equal
          specificity `px-4` won and the right padding stayed at 1rem. The
          Submit label was being clipped by the draggable support widget the
          whole time. A spacer element reserves the gutter instead, which no
          stylesheet ordering can undo. */}
      <div className="flex h-[74px] shrink-0 items-center justify-end gap-3 border-t border-slate-200 bg-white px-4 dark:border-gray-700 dark:bg-gray-800">
        <Button type="button" onClick={goBack} disabled={isBusy} className={"!bg-white !text-slate-600 border border-slate-200 hover:!bg-slate-100 focus:!ring-4 focus:!outline-none focus:!ring-slate-200 transition-all active:scale-95"}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={() => handleSave(false)}
          loading={saving === "draft"}
          disabled={isBusy}
          className={"!bg-slate-100 !text-slate-700 border border-slate-200 hover:!bg-slate-200 dark:!bg-gray-700 dark:!text-gray-100 dark:border-gray-600 dark:hover:!bg-gray-600 focus:!ring-4 focus:!outline-none focus:!ring-slate-200 transition-all active:scale-95 disabled:!opacity-50 disabled:!cursor-not-allowed"}
        >
          Save draft
        </Button>
        <Button
          type="button"

          onClick={() => handleSave(true)}
          loading={saving === "submit"}
          disabled={isBusy}
          className={"!bg-blue-700 !text-white hover:!bg-blue-800 dark:!bg-blue-600 dark:hover:!bg-blue-700 focus:!ring-4 focus:!outline-none focus:!ring-blue-300 dark:focus:!ring-blue-800 transition-all active:scale-95 disabled:!opacity-50 disabled:!cursor-not-allowed"}

        // className={"disabled:!opacity-50 disabled:!cursor-not-allowed"}
        >
          Submit for approval
        </Button>

        {/* Keeps the primary action clear of the floating support widget, which
            sits 64px wide at `innerWidth - 70` by default (SideBar/index.tsx).
            It is draggable, so this guarantees only the default position. */}
        <span aria-hidden="true" className="w-16 shrink-0" />
      </div>
    </div>
  );
};

export default GoalSheetFormPage;
